
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type PickingShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uBindShapeMatrix: WebGLUniformLocation,
	uMatrixSampler: WebGLUniformLocation,
	uDataSettings: WebGLUniformLocation,
	uBlendSettings: WebGLUniformLocation,
	uBoneOverrides: WebGLUniformLocation[],
	uBoneOverrideIndices: WebGLUniformLocation[],
	uBoneOverrideCount: WebGLUniformLocation,
	uBoneMatrix: WebGLUniformLocation,
	uPickingColor: WebGLUniformLocation
};

export type PickingShaderAttributes = {
	aVertexPosition: number,
	aJointWeights: number,
	aJointIndices: number
};

export type PickingShaderSettings = ('ANIMATION' | 'BONE_OVERRIDES' | 'ATTACHMENT')[];

export class PickingShader extends ShaderBase<PickingShaderUniforms, PickingShaderAttributes> {

    private static fragmentShaderSource = `
out vec4 fragColor;

uniform vec4 uPickingColor;

void main(void) {
	fragColor = uPickingColor;
}
`;

    private static vertexShaderSource = `
uniform mat4 uProjectionMatrix;
uniform mat4 uModelViewMatrix;

layout(location = 0) in vec3 aVertexPosition;

#ifdef NOODLE_ANIMATION
// --- begin: animation.glsl ---
layout(location = 4) in vec4 aJointWeights;
layout(location = 5) in vec4 aJointIndices;

uniform mat4 uBindShapeMatrix;
uniform sampler2D uMatrixSampler;
uniform vec3 uDataSettings;
uniform vec4 uBlendSettings;

#ifdef NOODLE_BONE_OVERRIDES
uniform mat4 uBoneOverrides[32];
uniform int uBoneOverrideIndices[32];
uniform int uBoneOverrideCount;
#endif

mat4 read_matrix(sampler2D tex, float i, vec2 size) {
	const float offset = 0.5;
	float tx = mod(i * 4.0, size.x) + offset;
	float ty = floor(i * 4.0 / size.x) + offset;

	return mat4(
		texture(tex, vec2((tx + 0.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 1.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 2.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 3.0) / size.x, ty / size.y))
	);
}

mat4 read_animation() {
	mat4 matrix = mat4(0.0);
	for (int j = 0; j < 4; j++) {
		float w = aJointWeights[j];
		if (w > 0.0) {
			float jointIndex = float(aJointIndices[j]);
			float index = jointIndex + uDataSettings.z;
			mat4 jointMatrix = read_matrix(uMatrixSampler, index, uDataSettings.xy);

			// Blend with previous clip if active
			if (uBlendSettings.w > 0.0) {
				mat4 prevMatrix = read_matrix(uMatrixSampler, jointIndex + uBlendSettings.z, uBlendSettings.xy);
				jointMatrix = prevMatrix + (jointMatrix - prevMatrix) * uBlendSettings.w;
			}

			#ifdef NOODLE_BONE_OVERRIDES
			for (int k = 0; k < uBoneOverrideCount; k++) {
				if (uBoneOverrideIndices[k] == int(jointIndex)) {
					jointMatrix = uBoneOverrides[k];
					break;
				}
			}
			#endif

			matrix += jointMatrix * w;
		}
	}
	return matrix;
}

// --- end: animation.glsl ---
#endif

#ifdef NOODLE_ATTACHMENT
uniform mat4 uBoneMatrix;
#endif

void main(void) {

#ifdef NOODLE_ANIMATION
	mat4 animationMatrix = read_animation();
	vec4 pos = vec4(aVertexPosition, 1.0) * uBindShapeMatrix * animationMatrix;
#else
	vec4 pos = vec4(aVertexPosition, 1.0);
#endif

#ifdef NOODLE_ATTACHMENT
	pos = pos * uBoneMatrix;
#endif

	gl_Position = uProjectionMatrix * uModelViewMatrix * vec4(pos.xyz, 1.0);
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: PickingShaderUniformsContainer;
    public attributesContainer: PickingShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: PickingShaderSettings) {
        super(ctx, PickingShader.fragmentShaderSource, PickingShader.vertexShaderSource, settings);
        this.uniformsContainer = new PickingShaderUniformsContainer(ctx, this);
        this.attributesContainer = new PickingShaderShaderAttributesContainer(ctx, this);
    }
}

class PickingShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: PickingShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setBindShapeMatrix(value: mat4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uBindShapeMatrix', this.shader.uniforms.uBindShapeMatrix, value, 'mat4');
        return this;
    }

    public setMatrixSampler(texture: Texture) : PickingShaderUniformsContainer {
        this.setTextureValue('uMatrixSampler', this.shader.uniforms.uMatrixSampler, texture, 'sampler2D');
        return this;
    }

    public setDataSettings(value: vec3 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uDataSettings', this.shader.uniforms.uDataSettings, value, 'vec3');
        return this;
    }

    public setBlendSettings(value: vec4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uBlendSettings', this.shader.uniforms.uBlendSettings, value, 'vec4');
        return this;
    }

    public setBoneOverrides(index: number, value: mat4 | number[]) : PickingShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrides: ' + index);
        this.setValue('uBoneOverrides', this.shader.uniforms.uBoneOverrides[index], value, 'mat4');
        return this;
    }

    public setBoneOverrideIndices(index: number, value: number) : PickingShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrideIndices: ' + index);
        this.setValue('uBoneOverrideIndices', this.shader.uniforms.uBoneOverrideIndices[index], value, 'int');
        return this;
    }

    public setBoneOverrideCount(value: number) : PickingShaderUniformsContainer {
        this.setValue('uBoneOverrideCount', this.shader.uniforms.uBoneOverrideCount, value, 'int');
        return this;
    }

    public setBoneMatrix(value: mat4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uBoneMatrix', this.shader.uniforms.uBoneMatrix, value, 'mat4');
        return this;
    }

    public setPickingColor(value: vec4 | number[]) : PickingShaderUniformsContainer {
        this.setValue('uPickingColor', this.shader.uniforms.uPickingColor, value, 'vec4');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMatrixSampler', uniform: this.shader.uniforms.uMatrixSampler, dataType: 'sampler2D' }
        ];
    }
}

class PickingShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: PickingShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : PickingShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointWeights(settings: { length: number, offset: number, itemSize: number }) : PickingShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointIndices(settings: { length: number, offset: number, itemSize: number }) : PickingShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointIndices, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}